/**
 * Results Schema - Zod validation for optimization results
 *
 * Validates the structure of optimization results from the solver.
 * Used to ensure results data integrity before display.
 *
 * @module schemas/results
 */

import { z } from 'zod';

/**
 * PV Results Schema
 */
export const PVResultsSchema = z.object({
    size_kw: z.number().min(0).default(0),
    year_one_energy_produced_kwh: z.number().min(0).optional(),
    annual_energy_produced_kwh: z.number().min(0).optional(),
    electric_to_grid_series_kw: z.array(z.number()).optional(),
    electric_to_load_series_kw: z.array(z.number()).optional(),
    electric_to_storage_series_kw: z.array(z.number()).optional(),
    electric_curtailed_series_kw: z.array(z.number()).optional(),
    capacity_factor: z.number().min(0).max(1).optional(),
    lcoe_per_kwh: z.number().min(0).optional(),
}).passthrough();

/**
 * Battery/Electric Storage Results Schema
 */
export const ElectricStorageResultsSchema = z.object({
    size_kw: z.number().min(0).default(0),
    size_kwh: z.number().min(0).default(0),
    soc_series_fraction: z.array(z.number()).optional(),
    storage_to_load_series_kw: z.array(z.number()).optional(),
    to_load_series_kw: z.array(z.number()).optional(),
    grid_to_storage_series_kw: z.array(z.number()).optional(),
    year_one_soc_series_pct: z.array(z.number()).optional(),
    degradation: z.object({
        annual_cycles: z.number().optional(),
        annual_throughput_kwh: z.number().optional(),
        years_to_80_pct: z.number().nullable().optional(),
        eol_capacity_pct: z.number().optional(),
        maintenance_cost: z.number().optional(),
    }).optional(),
}).passthrough();

/**
 * Generator Results Schema
 */
export const GeneratorResultsSchema = z.object({
    size_kw: z.number().min(0).default(0),
    annual_fuel_consumption_gal: z.number().min(0).optional(),
    electric_to_load_series_kw: z.array(z.number()).optional(),
    electric_to_storage_series_kw: z.array(z.number()).optional(),
    year_one_fuel_cost_us_dollars: z.number().optional(),
}).passthrough();

/**
 * CHP Results Schema
 */
export const CHPResultsSchema = z.object({
    size_kw: z.number().min(0).default(0),
    annual_fuel_consumption_mmbtu: z.number().min(0).optional(),
    annual_thermal_production_mmbtu: z.number().min(0).optional(),
    electric_to_load_series_kw: z.array(z.number()).optional(),
    thermal_to_load_series_mmbtu_per_hour: z.array(z.number()).optional(),
}).passthrough();

/**
 * Grid Results Schema
 */
export const ElectricUtilityResultsSchema = z.object({
    annual_energy_supplied_kwh: z.number().optional(),
    electric_to_load_series_kw: z.array(z.number()).optional(),
    annual_emissions_tonnes_CO2: z.number().optional(),
    year_one_bill_before_incentives: z.number().optional(),
}).passthrough();

/**
 * Site/System Results Schema
 */
export const SiteResultsSchema = z.object({
    annual_renewable_electricity_kwh: z.number().optional(),
    renewable_electricity_fraction: z.number().min(0).max(1).optional(),
    total_renewable_energy_fraction: z.number().min(0).max(1).optional(),
    survivability: z.object({
        min_hours: z.number().optional(),
        avg_hours: z.number().optional(),
        critical_load_met_pct: z.number().optional(),
        battery_kwh: z.number().optional(),
        critical_load_kw: z.number().optional(),
    }).optional(),
}).passthrough();

/**
 * Financial Results Schema
 */
export const FinancialResultsSchema = z.object({
    lcc: z.number().optional(),
    lcc_bau: z.number().optional(),
    npv: z.number().optional(),
    simple_payback_years: z.number().nullable().optional(),
    internal_rate_of_return: z.number().nullable().optional(),
    net_capital_costs: z.number().optional(),
    om_and_replacement_present_cost_after_tax: z.number().optional(),
    total_om_costs_us_dollars: z.number().optional(),
    year_one_om_costs_before_tax_us_dollars: z.number().optional(),
    lifecycle_generation_tech_capital_costs: z.number().optional(),
    lifecycle_storage_capital_costs: z.number().optional(),
    lifecycle_chp_capital_costs: z.number().optional(),
    initial_capital_costs: z.number().optional(),
    initial_capital_costs_after_incentives: z.number().optional(),
    federal_itc: z.number().optional(),
}).passthrough();

/**
 * Main Outputs Schema
 */
export const OutputsSchema = z.object({
    PV: PVResultsSchema.optional(),
    ElectricStorage: ElectricStorageResultsSchema.optional(),
    Generator: GeneratorResultsSchema.optional(),
    CHP: CHPResultsSchema.optional(),
    ElectricUtility: ElectricUtilityResultsSchema.optional(),
    Site: SiteResultsSchema.optional(),
    Financial: FinancialResultsSchema.optional(),
}).passthrough();

/**
 * Full Results Schema
 * Validates the complete optimization response
 */
export const ResultsSchema = z.object({
    status: z.enum([
        'optimal',
        'Optimal Obtained',
        'completed',
        'error',
        'Error',
        'failed',
        'Failed',
        'timed_out',
        'infeasible',
        'running',
        'Running',
        'queued',
        'Queued',
    ]).optional(),
    run_uuid: z.string().optional(),
    outputs: OutputsSchema.optional(),
    inputs: z.record(z.any()).optional(),
    messages: z.object({
        errors: z.array(z.string()).optional(),
        warnings: z.array(z.string()).optional(),
    }).optional(),
    bau_results: z.record(z.any()).optional(),
    infeasibility_metrics: z.object({
        configured_limit_kw: z.number().optional(),
        min_required_limit_kw: z.number().optional(),
        peak_load_kw: z.number().optional(),
        canFindMinimum: z.boolean().optional(),
    }).optional(),
    timestamp: z.number().optional(),
}).passthrough();

/**
 * Validate results data
 *
 * @param {Object} data - Results data to validate
 * @returns {{ success: boolean, data?: Object, error?: Object }}
 */
export function validateResults(data) {
    return ResultsSchema.safeParse(data);
}

/**
 * Extract key metrics from results for display
 *
 * @param {Object} results - Validated results object
 * @returns {Object} Key metrics
 */
export function extractKeyMetrics(results) {
    if (!results?.outputs) {
        return null;
    }

    const outputs = results.outputs;
    const financial = outputs.Financial || {};
    const pv = outputs.PV || {};
    const battery = outputs.ElectricStorage || {};
    const generator = outputs.Generator || {};
    const chp = outputs.CHP || {};
    const site = outputs.Site || {};

    return {
        // System sizing
        pvSizeKw: pv.size_kw || 0,
        batterySizeKw: battery.size_kw || 0,
        batterySizeKwh: battery.size_kwh || 0,
        generatorSizeKw: generator.size_kw || 0,
        chpSizeKw: chp.size_kw || 0,

        // Financial
        npv: financial.npv || 0,
        paybackYears: financial.simple_payback_years,
        irr: financial.internal_rate_of_return,
        netCapitalCosts: financial.net_capital_costs || 0,
        lcc: financial.lcc || 0,
        lccBau: financial.lcc_bau || 0,
        annualSavings: (financial.lcc_bau || 0) - (financial.lcc || 0),

        // Energy
        pvAnnualKwh: pv.annual_energy_produced_kwh || pv.year_one_energy_produced_kwh || 0,
        renewableFraction: site.renewable_electricity_fraction || 0,

        // Resilience
        survivabilityHours: site.survivability?.avg_hours || 0,
    };
}

export default ResultsSchema;
